<template>
  <div class="app-radio-group" :class="rootClasses">
    <div v-if="label" class="app-radio-group__label">
      {{ label }}
      <span v-if="showRequiredMark" class="app-radio-group__required" aria-hidden="true">*</span>
    </div>

    <div class="app-radio-group__items" :style="layoutStyle">
      <label
        v-for="(opt, idx) in options"
        :key="getValue(opt)"
        class="app-radio"
        :class="{ 'app-radio--disabled': isDisabled(opt) }"
      >
        <input
          type="radio"
          :name="groupName"
          :value="getValue(opt)"
          :checked="isChecked(opt)"
          :disabled="isDisabled(opt)"
          class="app-radio__input"
          @change="onPick(opt, idx, $event)"
        />
        <span class="app-radio__bullet"></span>
        <span class="app-radio__label">
          <slot name="option" :option="opt" :index="idx">
            {{ getLabel(opt) }}
          </slot>
        </span>
      </label>
    </div>

    <small
      v-if="error"
      class="app-radio-group__error"
      v-html="error"
    ></small>
    <small
      v-else-if="help"
      class="app-radio-group__help"
      v-html="help"
    ></small>
  </div>
</template>

<script>
// Auto-generated group name when caller didn't supply one — radio inputs
// rely on a shared `name` attr for native single-selection grouping.
let uid = 0

/**
 * App-styled radio group — no-Bootstrap, modern v-model contract.
 *
 * Mirrors `c-radio-group` API plus quality-of-life additions:
 *   value, options, name, error, help, cols, gap, disabled, labelKey,
 *   valueKey, size, direction, required.
 *
 * Slots:
 *   option — scoped slot ({ option, index }) for custom per-option label.
 */
export default {
  name: 'AppRadioGroup',

  props: {
    value:        { type: [String, Number, Boolean, null], default: null },
    options:      { type: Array,   required: true },
    name:         { type: String,  default: '' },
    label:        { type: String,  default: '' },
    help:         { type: String,  default: '' },
    error:        { type: String,  default: '' },
    valueKey:     { type: String,  default: 'value' },
    labelKey:     { type: String,  default: 'label' },
    disabledKey:  { type: String,  default: 'disabled' },
    cols:         { type: Number,  default: 0 },
    gap:          { type: String,  default: '8px 16px' },
    direction: {
      type: String,
      default: 'row',
      validator: (v) => ['row', 'column'].includes(v),
    },
    disabled:     { type: Boolean, default: false },
    required:     { type: Boolean, default: false },
    requiredMark: { type: Boolean, default: false },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
  },

  emits: ['input', 'change'],

  data() {
    return {
      autoName: `app-radio-group-${++uid}`,
    }
  },

  computed: {
    groupName()       { return this.name || this.autoName },
    showRequiredMark() { return this.requiredMark || this.required },

    layoutStyle() {
      if (this.cols > 0) {
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${this.cols}, auto)`,
          gap: this.gap,
          justifyContent: 'flex-start',
        }
      }
      return {
        display: 'flex',
        flexDirection: this.direction === 'column' ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: this.gap,
      }
    },

    rootClasses() {
      return [
        `app-radio-group--${this.size}`,
        {
          'app-radio-group--has-error': !!this.error,
          'app-radio-group--disabled': this.disabled,
        },
      ]
    },
  },

  methods: {
    getValue(opt)   { return opt[this.valueKey] ?? opt.value },
    getLabel(opt)   { return opt[this.labelKey] ?? opt.label ?? opt.text ?? '' },
    isDisabled(opt) { return this.disabled || !!opt[this.disabledKey] },

    isChecked(opt) {
      // Loose equality intentional — see app-radio for reasoning.
      // eslint-disable-next-line eqeqeq
      return this.getValue(opt) == this.value
    },

    onPick(opt, idx, event) {
      const v = this.getValue(opt)
      this.$emit('input', v)
      this.$emit('change', v, opt, event)
    },
  },
}
</script>

<style lang="scss" scoped>
$arg-text:       #2b3a4a;
$arg-text-muted: #6c7a89;
$arg-bg-surface: #ffffff;
$arg-bg-soft:    #f6f8fa;
$arg-border:     #cfd6dc;
$arg-blue:       #1f73c1;
$arg-blue-focus: #4392d4;
$arg-red:        #d04848;
$arg-duration:   140ms;
$arg-ease:       cubic-bezier(.4, 0, .2, 1);

.app-radio-group {
  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $arg-text-muted;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
    line-height: 1.2;
  }

  &__required {
    color: $arg-red;
    margin-left: 2px;
  }

  &__items { min-width: 0; }

  &__error,
  &__help {
    display: block;
    font-size: 11px;
    margin-top: 6px;
    line-height: 1.4;
  }
  &__error { color: $arg-red; }
  &__help  { color: $arg-text-muted; }

  &--disabled { opacity: 0.7; pointer-events: none; }
}

// ── Radio item styling (mirrors app-radio for visual consistency) ──
.app-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &--disabled { cursor: not-allowed; opacity: 0.7; }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px; height: 1px; margin: -1px;
  }

  &__bullet {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: $arg-bg-surface;
    border: 1px solid $arg-border;
    position: relative;
    transition: background $arg-duration $arg-ease, border-color $arg-duration $arg-ease, box-shadow $arg-duration $arg-ease;

    &::after {
      content: '';
      position: absolute;
      top: 50%; left: 50%;
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #fff;
      transform: translate(-50%, -50%) scale(0);
      transition: transform $arg-duration $arg-ease;
    }
  }

  &__label {
    font-size: 13px;
    color: $arg-text;
    line-height: 1.4;
  }

  &:hover &__bullet { border-color: $arg-blue-focus; }

  &__input:checked + &__bullet {
    background: $arg-blue;
    border-color: $arg-blue;

    &::after { transform: translate(-50%, -50%) scale(1); }
  }

  &__input:focus-visible + &__bullet {
    box-shadow: 0 0 0 3px rgba(67, 146, 212, 0.20);
  }

  &__input:disabled + &__bullet {
    background: $arg-bg-soft;
    border-color: rgba(0, 0, 0, 0.10);
  }
}

// Size variants
.app-radio-group--sm {
  .app-radio__bullet { width: 14px; height: 14px; }
  .app-radio__bullet::after { width: 6px; height: 6px; }
  .app-radio__label { font-size: 12px; }
}

// Large variant — mirrors app-checkbox `lg` (22px control, 14px label) so
// radios and checkboxes line up in the same form grid.
.app-radio-group--lg {
  .app-radio { gap: 10px; }
  .app-radio__bullet { width: 22px; height: 22px; border-width: 1.5px; }
  .app-radio__bullet::after { width: 11px; height: 11px; }
  .app-radio__label { font-size: 14px; }

  .app-radio__input:focus-visible + .app-radio__bullet {
    box-shadow: 0 0 0 4px rgba(67, 146, 212, 0.22);
  }
}

.app-radio-group--has-error {
  .app-radio__bullet { border-color: $arg-red; }
}
</style>
